import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import {
  NgxExtendedPdfViewerModule,
  NgxExtendedPdfViewerService,
  PDFNotificationService,
  PdfSidebarView,
  RenderedTextLayerHighlights,
} from 'ngx-extended-pdf-viewer';
import * as fromFeatureFlags from '../../../feature-flags';
import {
  getThumbnails,
  getThumbnailsWithPins,
} from './document-viewer-content.helper';
import { resolvePromiseWithTimeout } from './document-viewer.helper';

export type DocumentViewerZoomSetting = string | number | undefined;
export type DocumentViewerZoomFactor = number | undefined;

@Component({
  selector: 'squadbox-document-viewer-content',
  imports: [
    NgxExtendedPdfViewerModule,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
  ],
  host: { '[class.is-mobile]': 'isMobile()' },
  template: `
    <!-- Spinner -->
    <div
      class="absolute inset-0 z-10 flex items-center justify-center bg-transparency-black-16"
      [class.hidden]="!isLoading()"
    >
      <div sdSpinner size="md"></div>
    </div>
    <!-- PDF Viewer -->
    <ngx-extended-pdf-viewer
      [class.show-jumper-background]="pagesWithVisiblePins().length > 0"
      [src]="src()"
      [enableDragAndDrop]="false"
      [showToolbar]="false"
      [sidebarVisible]="!isMobile()"
      [activeSidebarView]="PdfSidebarView.THUMBS"
      [showBorders]="false"
      [handTool]="false"
      [customSidebar]="customSidebar"
      [textLayer]="ffDocumentViewerHighlightText()"
      (pdfLoadingStarts)="isPdfLoaded.set(false)"
      (pdfLoaded)="isPdfLoaded.set(true)"
      (currentZoomFactor)="zoomFactor.set($event)"
      [minZoom]="zoomFactorLimits().min"
      [maxZoom]="zoomFactorLimits().max"
      [zoom]="zoomSetting()"
      (zoomChange)="zoomSetting.set($event)"
      [(page)]="page"
    ></ngx-extended-pdf-viewer>
    <!-- PDF Viewer: Sidebar -->
    <ng-template #customSidebar>
      <pdf-sidebar-content
        id="sidebarContainer"
        class="z-20"
        [customThumbnail]="customThumbnail"
        [hideSidebarToolbar]="true"
      ></pdf-sidebar-content>
    </ng-template>
    <!-- PDF Viewer: Thumbnail -->
    <ng-template #customThumbnail>
      <a class="pdf-viewer-template">
        <div class="thumbnail" data-page-number="PAGE_NUMBER">
          <!-- PDF Viewer: Thumbnail: Pin (hidden by default) -->
          <div class="pin" hidden>
            <svg>
              <use href="/assets/ui-basics.sprites.svg#quote"></use>
            </svg>
          </div>
          <div class="image-container">
            <img
              class="thumbnailImage"
              [alt]="'CHAT.DOCUMENT_VIEWER.PAGE_THUMBNAIL_ALT' | translate"
            />
          </div>
          <div class="page-number">PAGE_NUMBER</div>
        </div>
      </a>
    </ng-template>
  `,
  styles: [
    `
      @reference '../../../../styles.css';

      :host {
        @apply relative w-full grow;
        @apply typo-h1 bg-neutral-100;
        @apply flex items-center justify-center;
      }
    `,
  ],
})
export class DocumentViewerContentComponent {
  public readonly src = input.required<string>();
  public readonly relevantPages = input.required<number[]>();
  public readonly relevantPagesWithCitationsMatches = signal<number[]>([]);
  public readonly page = model<number>(1);
  public readonly relevantTexts = input.required<string[]>();
  public readonly zoomSetting = model.required<DocumentViewerZoomSetting>();
  public readonly zoomFactor = model.required<DocumentViewerZoomFactor>();
  public readonly zoomFactorLimits = input.required<{
    min: number;
    max: number;
  }>();
  public readonly isMobile = input<boolean>(false);
  public readonly isCitationsLoading = input.required<boolean>();

  public readonly PdfSidebarView = PdfSidebarView;
  private readonly ngxExtendedPdfViewerService = inject(
    NgxExtendedPdfViewerService,
  );
  private readonly pdfNotificationService = inject(PDFNotificationService);

  private readonly featureFlagsStore =
    inject<Store<fromFeatureFlags.FeatureFlagsState>>(Store);
  public readonly ffDocumentViewerHighlightText =
    this.featureFlagsStore.selectSignal(
      fromFeatureFlags.featureFlagsFeature.selectFeatureFlag(
        'ff-document-viewer-highlight-text',
      ),
    );

  public readonly isLoading = model<boolean>(false);

  public readonly isPdfLoaded = signal(false);
  private readonly haveRelevantPagesBeenRendered = signal(false);
  private readonly isTextSearchComplete = signal(false);

  private readonly isReadyForTextSearch = computed(
    () =>
      this.ffDocumentViewerHighlightText() &&
      this.isPdfLoaded() &&
      !this.isCitationsLoading() &&
      this.haveRelevantPagesBeenRendered(),
  );

  private readonly elementRef = inject<ElementRef<HTMLDivElement>>(ElementRef);

  public readonly pagesWithVisiblePins = model<number[]>([]);

  constructor() {
    effect(() => {
      if (this.src()) {
        this.reset();
      }
    });

    effect(() => {
      if (this.ffDocumentViewerHighlightText()) {
        // Since the find is done on all pages, we can have matches that are on irrelevant pages and we don't want to highlight them.
        this.subscribeToRemoveHighlightedTextsOnIrrelevantPages();
      }
    });

    effect(() => {
      if (this.isPdfLoaded() && this.ffDocumentViewerHighlightText()) {
        void this.preRenderRelevantPages();
      }
    });

    // Run the find only if PDF is loaded, citations are loaded, and highlight ff is enabled
    effect(() => {
      if (this.isReadyForTextSearch()) {
        void this.findRelevantTexts().finally(() => {
          this.isTextSearchComplete.set(true);
        });
      }
    });

    /**
     * Draw pins on the thumbnails of the relevant pages with citations matches,
     * or fallback to drawing pins on all relevant pages if there are no citations matches.
     */
    effect(() => {
      if (this.ffDocumentViewerHighlightText() && this.isTextSearchComplete()) {
        const relevantPagesWithCitationsMatches =
          this.relevantPagesWithCitationsMatches();
        const relevantPages = this.relevantPages();
        /**
         * If there are citations matches, we draw pins on the thumbnails of the relevant
         * pages with citations matches only.
         */
        if (relevantPagesWithCitationsMatches.length > 0) {
          this.scrollToFirstPage(this.relevantPagesWithCitationsMatches());
          this.hidePinsOnAllPages();
          return this.drawPinsOnPages(relevantPagesWithCitationsMatches);
        }
        /**
         * If there are no citations matches, we draw pins on all relevant pages.
         */
        if (this.relevantPages().length > 0) {
          this.scrollToFirstPage(this.relevantPages());
          return this.drawPinsOnPages(relevantPages);
        }
      }
    });

    effect(() => {
      if (!this.isPdfLoaded()) {
        this.isLoading.set(true);
      } else if (
        this.ffDocumentViewerHighlightText() &&
        !this.isTextSearchComplete()
      ) {
        this.isLoading.set(true);
      } else {
        this.isLoading.set(false);
      }
    });
  }

  private drawPinsOnPages(pages: number[]) {
    const thumbnailsWithPins = getThumbnails(this.elementRef.nativeElement);
    const pagesWithPins: number[] = [];

    thumbnailsWithPins.forEach((thumbnailWithPin) => {
      if (pages.includes(thumbnailWithPin.pageNumber())) {
        thumbnailWithPin.showPin();
        pagesWithPins.push(thumbnailWithPin.pageNumber());
      }
    });

    this.pagesWithVisiblePins.set(pagesWithPins);
  }

  private hidePinsOnAllPages() {
    const thumbnailsWithPins = getThumbnailsWithPins(
      this.elementRef.nativeElement,
    );

    thumbnailsWithPins.forEach((thumbnailWithPin) => {
      thumbnailWithPin.hidePin();
    });

    this.pagesWithVisiblePins.set([]);
  }

  private reset() {
    this.isPdfLoaded.set(false);
    this.haveRelevantPagesBeenRendered.set(false);
    this.isTextSearchComplete.set(false);
    this.relevantPagesWithCitationsMatches.set([]);
    this.page.set(1);
  }

  private async preRenderRelevantPages() {
    for (const page of this.relevantPages()) {
      const pageIndex = page - 1;
      if (!this.ngxExtendedPdfViewerService.hasPageBeenRendered(pageIndex)) {
        // This timeout is here to wait for the render queue to be ready to process new renders
        await new Promise((resolve) => setTimeout(resolve, 100));
        // We resolve with a timeout in case the above timeout wasn't enough
        await resolvePromiseWithTimeout(
          this.ngxExtendedPdfViewerService.renderPage(pageIndex),
          undefined,
        );
      }
    }

    this.haveRelevantPagesBeenRendered.set(true);
  }

  private scrollToFirstPage(pageNumbers: number[]) {
    if (pageNumbers.length) {
      const firstPage = Math.min(...pageNumbers);
      this.page.set(firstPage);
    }
  }

  private async findRelevantTexts() {
    const matchCountPerPagePromises = this.ngxExtendedPdfViewerService.find(
      this.relevantTexts(),
      {
        useSecondaryFindcontroller: true,
        highlightAll: true,
        findMultiple: true,
        // We disable the scroll into view because a match can possibly be found in a page that comes
        // before the relevant pages
        dontScrollIntoView: true,
      },
    );

    if (!matchCountPerPagePromises) {
      return;
    }

    await Promise.all(
      matchCountPerPagePromises
        .filter((_, index) => this.relevantPages().includes(index + 1))
        .map((matchCountPerPagePromise, pageIndex) =>
          this.processPageMatches(matchCountPerPagePromise, pageIndex),
        ),
    );
  }

  private async processPageMatches(
    matchCountPerPagePromise: Promise<number>,
    pageIndex: number,
  ): Promise<void> {
    if (this.relevantPages().includes(pageIndex)) {
      // We observed that sometimes, promises were not resolving on large documents, hence why we
      // added a timeout as a workaround.
      await resolvePromiseWithTimeout(matchCountPerPagePromise, 0);
    }
  }

  /**
   * We subscribe to the event `renderedtextlayerhighlights` to remove the highlights
   * from the irrelevant pages and to update the list of relevant pages with citations matches.
   */
  private subscribeToRemoveHighlightedTextsOnIrrelevantPages() {
    const pdfViewerApplication =
      this.pdfNotificationService.onPDFJSInitSignal();

    pdfViewerApplication?.eventBus?.on(
      'renderedtextlayerhighlights',
      (event: RenderedTextLayerHighlights) => {
        const pageNumber = event.pageIndex + 1;

        /**
         * 1. We need to update the list of relevant pages with citations matches
         * because the text search is done on all pages and we need to know which
         * pages have matches to hide the pins on the irrelevant pages.
         */
        if (
          event.highlights.length > 0 &&
          this.relevantPages().includes(pageNumber) &&
          !this.relevantPagesWithCitationsMatches().includes(pageNumber)
        ) {
          this.relevantPagesWithCitationsMatches.update((prev) => [
            ...prev,
            pageNumber,
          ]);
        }

        event.highlights.forEach((highlight) => {
          /**
           * 2. We remove the highlight from the irrelevant pages.
           */
          if (
            !this.relevantPages().includes(pageNumber) &&
            highlight.classList.contains('customHighlight')
          ) {
            highlight.classList.remove('customHighlight');
          }
        });
      },
    );
  }
}
